import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useInventoryStore } from "../../../store/useInventoryStore";
import {
  safeNumber,
  normalizeNumber,
} from "../../../utils/inventory/inventoryHelpers";



const inputClassName =
  "w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-400";

const InventoryRow = ({ product, onSaveRow, saving }) => {
  const rowDrafts = useInventoryStore((state) => state.rowDrafts);
  const updateRowDraftField = useInventoryStore(
    (state) => state.updateRowDraftField
  );

  const UNIT_TRANSLATIONS = {
    count: "عدد",
    package: "بسته",
    weight: "کیلوگرم",
    length: "متر",
    area: "متر مربع",
    litr: "لیتر",
    volume: "متر مکعب",
  };

  const draft = rowDrafts[product.id] || {
    price: product.price ?? "",
    stock: product.stock ?? "",
    discount_percent: product.discount_percent ?? "",
    min_order: product.min_order ?? 1,
    max_order: product.max_order ?? "",
    order_multiple: product.order_multiple ?? 1,
    inventory_is_active: product.inventory_is_active ?? true,
    originalData: {
      price: product.price ?? "",
      stock: product.stock ?? "",
      discount_percent: product.discount_percent ?? "",
      min_order: product.min_order ?? 1,
      max_order: product.max_order ?? "",
      order_multiple: product.order_multiple ?? 1,
      inventory_is_active: product.inventory_is_active ?? true,
    },
    isDirty: false,
  };

  const isDirty = useMemo(() => {
    const current = {
      price: normalizeNumber(draft.price, ""),
      stock: normalizeNumber(draft.stock, ""),
      discount_percent: normalizeNumber(draft.discount_percent, ""),
      min_order: normalizeNumber(draft.min_order, ""),
      max_order: normalizeNumber(draft.max_order, ""),
      order_multiple: normalizeNumber(draft.order_multiple, ""),
      inventory_is_active: Boolean(draft.inventory_is_active),
    };

    const original = {
      price: normalizeNumber(draft.originalData?.price, ""),
      stock: normalizeNumber(draft.originalData?.stock, ""),
      discount_percent: normalizeNumber(draft.originalData?.discount_percent, ""),
      min_order: normalizeNumber(draft.originalData?.min_order, ""),
      max_order: normalizeNumber(draft.originalData?.max_order, ""),
      order_multiple: normalizeNumber(draft.originalData?.order_multiple, ""),
      inventory_is_active: Boolean(draft.originalData?.inventory_is_active),
    };

    return JSON.stringify(current) !== JSON.stringify(original);
  }, [draft]);

  const handleChange = (field, value) => {
    updateRowDraftField(product.id, field, value);
  };

  const handleSave = async () => {
    const payload = {
      price: safeNumber(draft.price, 0),
      stock: safeNumber(draft.stock, 0),
      discount_percent: safeNumber(draft.discount_percent, 0),
      min_order: safeNumber(draft.min_order, 1),
      max_order: draft.max_order === "" ? null : safeNumber(draft.max_order, null),
      order_multiple: safeNumber(draft.order_multiple, 1),
      is_active: Boolean(draft.inventory_is_active),
    };

    try {
      await onSaveRow(product.id, payload);
    } catch (err) {
      console.error("خطا در ذخیره ردیف:", err);
    }
  };

  return (
    <tr className="align-middle transition hover:bg-gray-50">
      <td className="border-b px-3 py-2">
        <Link
          to={`/supplier-panel/supplier-product/${product.id}`}
          title="مشاهده جزئیات کالا"
          className="block"
        >
          <img
            src={product.featured_image_url || "/placeholder-product.png"}
            alt={product.name}
            className="h-12 w-12 rounded-lg border border-gray-200 object-cover"
          />
        </Link>
      </td>

      <td className="border-b px-3 py-2">
        <div className="flex flex-col gap-1.5 text-xs text-gray-500">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <span
                className="max-w-[150px] truncate text-sm font-semibold text-gray-900"
                title={product.name}
              >
                {product.name}
              </span>

              {!product.is_active && (
                <span className="shrink-0 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-600">
                  غیرفعال
                </span>
              )}
            </div>

            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-700">
              {UNIT_TRANSLATIONS[product.unit_of_measure] || "---"}
            </span>
          </div>

          <div className="text-[11px] text-gray-400">
            {product.product_code || "---"}
          </div>

          <div className="flex items-center justify-between gap-2 text-[11px]">
            <span className="truncate rounded bg-blue-50 px-1 text-blue-700">
              {product.category_name || "---"}
            </span>
            <span className="truncate text-gray-400">
              برند:{" "}
              <strong className="font-medium text-gray-600">
                {product.brand || "---"}
              </strong>
            </span>
          </div>
        </div>
      </td>

      <td className="border-b px-3 py-2">
        <input
          type="text"
          inputMode="numeric"
          value={draft.price}
          onChange={(e) => handleChange("price", e.target.value)}
          placeholder="0"
          className={inputClassName}
        />
      </td>

      <td className="border-b px-3 py-2">
        <input
          type="text"
          inputMode="numeric"
          value={draft.stock}
          onChange={(e) => handleChange("stock", e.target.value)}
          placeholder="0"
          className={inputClassName}
        />
      </td>

      <td className="border-b px-3 py-2">
        <input
          type="text"
          inputMode="numeric"
          value={draft.discount_percent}
          onChange={(e) => handleChange("discount_percent", e.target.value)}
          placeholder="0"
          className={inputClassName}
        />
      </td>

      <td className="border-b px-3 py-2">
        <input
          type="text"
          inputMode="numeric"
          value={draft.min_order}
          onChange={(e) => handleChange("min_order", e.target.value)}
          placeholder="1"
          className={inputClassName}
        />
      </td>

      <td className="border-b px-3 py-2">
        <input
          type="text"
          inputMode="numeric"
          value={draft.max_order}
          onChange={(e) => handleChange("max_order", e.target.value)}
          placeholder=""
          className={inputClassName}
        />
      </td>

      <td className="border-b px-3 py-2">
        <input
          type="text"
          inputMode="numeric"
          value={draft.order_multiple}
          onChange={(e) => handleChange("order_multiple", e.target.value)}
          placeholder="1"
          className={inputClassName}
        />
      </td>

      <td className="border-b px-3 py-2 text-center">
        <div className="flex items-center justify-center">
          <label className="relative inline-flex cursor-pointer items-center">
            <input
              type="checkbox"
              checked={Boolean(draft.inventory_is_active)}
              onChange={(e) =>
                handleChange("inventory_is_active", e.target.checked)
              }
              className="peer sr-only"
            />
            <div className="h-6 w-11 rounded-full bg-gray-200 transition-colors after:absolute after:start-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-green-600 peer-checked:after:translate-x-full peer-checked:after:border-white rtl:peer-checked:after:-translate-x-full" />
          </label>
        </div>
      </td>

      <td className="border-b px-3 py-2 text-center">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !isDirty}
          className={`w-20 rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
            saving || !isDirty
              ? "cursor-not-allowed bg-gray-200 text-gray-500"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          {saving ? (
            <span className="flex items-center justify-center gap-1">
              <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
              <span>...</span>
            </span>
          ) : (
            "ذخیره"
          )}
        </button>
      </td>
    </tr>
  );
};

export default InventoryRow;
